/**
 * Recipe detail — the reference screen from DESIGN_GUIDELINES.md §8.
 *
 * Everything rendered here comes from the local database and `/api/images`. After import the page
 * makes ZERO requests to HelloFresh, which is the product's core promise and is asserted in tests.
 *
 * The serving switcher re-reads stored quantities for the selected yield rather than scaling in
 * the browser: HelloFresh's scaling is not linear (lemon is 1/1/2 units for 2/3/4 servings), so
 * arithmetic would produce wrong shopping quantities.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { formatQuantity } from '@hf-planner/shared';
import { PageShell } from '../components/PageShell';
import {
  createList,
  deleteRecipe,
  fetchRecipe,
  imageUrl,
  updateRecipeSettings,
  type RecipeDetail,
  type SessionUser,
} from '../lib/api';
import { useAsync } from '../lib/useAsync';

const PANTRY_BG = 'var(--ff-green-050)';

function minutesLabel(minutes: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

/** Nutrition rows that read as a headline summary. */
const MACRO_NAMES = ['Carbohydrate', 'Protein', 'Fat', 'Dietary Fibre'];

export function RecipeDetailPage({
  user,
  onSignOut,
}: {
  user: SessionUser;
  onSignOut: () => void;
}) {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const detail = useAsync(() => fetchRecipe(id), [id]);
  const [servings, setServings] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const recipe: RecipeDetail | null = detail.data;

  const activeServings = useMemo(() => {
    if (!recipe) return servings ?? 2;
    if (servings !== null && recipe.yields.some((y) => y.servings === servings)) return servings;
    return recipe.yields.find((y) => y.isDefault)?.servings ?? recipe.yields[0]?.servings ?? 2;
  }, [recipe, servings]);

  /** (hfId -> formatted amount) for the selected serving size. */
  const amounts = useMemo(() => {
    const map = new Map<string, string>();
    if (!recipe) return map;
    for (const quantity of recipe.quantities) {
      if (quantity.servings !== activeServings) continue;
      if (quantity.amountBase === null || !quantity.unitNormalized) continue;
      map.set(quantity.hfId, formatQuantity(quantity.amountBase, quantity.unitNormalized));
    }
    return map;
  }, [recipe, activeServings]);

  async function toggleFavourite() {
    if (!recipe) return;
    const next = !recipe.isFavourite;
    detail.setData({ ...recipe, isFavourite: next });
    try {
      await updateRecipeSettings(recipe.recipe.id, { isFavourite: next });
    } catch {
      detail.reload();
    }
  }

  async function handleDelete() {
    if (!recipe) return;
    setBusy(true);
    try {
      await deleteRecipe(recipe.recipe.id);
      navigate('/');
    } catch {
      setNotice('Could not remove the recipe.');
      setBusy(false);
    }
  }

  async function addToShoppingList() {
    if (!recipe) return;
    setBusy(true);
    try {
      const created = await createList(recipe.recipe.name, [
        { recipeId: recipe.recipe.id, servings: activeServings },
      ]);
      navigate(`/lists/${created.id}`);
    } catch {
      setNotice('Could not build a shopping list.');
      setBusy(false);
    }
  }

  if (detail.loading) {
    return (
      <PageShell user={user} onSignOut={onSignOut}>
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </PageShell>
    );
  }

  if (detail.error || !recipe) {
    return (
      <PageShell user={user} onSignOut={onSignOut}>
        <Alert color="red" variant="light" title="Recipe unavailable" role="alert">
          {detail.error ?? 'This recipe is not in your collection.'}
        </Alert>
        <Anchor component={Link} to="/" mt="md" display="block">
          ← Back to your recipes
        </Anchor>
      </PageShell>
    );
  }

  const src = imageUrl(recipe.recipe.imageKey);
  const boxed = recipe.ingredients.filter((i) => i.shipped).sort((a, b) => a.position - b.position);
  const pantry = recipe.ingredients.filter((i) => !i.shipped).sort((a, b) => a.position - b.position);
  const trueAllergens = recipe.allergens.filter((a) => !a.tracesOf);
  const traces = recipe.allergens.filter((a) => a.tracesOf);

  return (
    <PageShell
      user={user}
      onSignOut={onSignOut}
      title={
        <Stack gap={2}>
          <Anchor component={Link} to="/" size="sm" c="var(--ff-muted)" td="none">
            ← Back
          </Anchor>
          <Title order={2}>{recipe.recipe.name}</Title>
          {recipe.recipe.headline ? (
            <Text c="var(--ff-muted)" size="sm">
              {recipe.recipe.headline}
            </Text>
          ) : null}
        </Stack>
      }
      actions={
        <Group gap="xs">
          <ActionIcon
            variant={recipe.isFavourite ? 'filled' : 'light'}
            color={recipe.isFavourite ? 'red' : 'gray'}
            radius="xl"
            size="lg"
            aria-pressed={recipe.isFavourite}
            aria-label={recipe.isFavourite ? 'Remove from favourites' : 'Add to favourites'}
            onClick={() => void toggleFavourite()}
          >
            <span aria-hidden="true">{recipe.isFavourite ? '♥' : '♡'}</span>
          </ActionIcon>
          <Button variant="light" color="gray" radius="xl" loading={busy} onClick={() => void handleDelete()}>
            Remove
          </Button>
        </Group>
      }
    >
      {notice ? (
        <Alert color="red" variant="light" role="alert">
          {notice}
        </Alert>
      ) : null}

      {recipe.recipe.isPartial ? (
        <Alert color="yellow" variant="light" title="Imported with reduced detail">
          This recipe was imported without its serving-size data, so quantities are for the default
          serving count only.
        </Alert>
      ) : null}

      {src ? (
        <img
          src={src}
          alt={recipe.recipe.name}
          style={{
            width: '100%',
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            borderRadius: 'var(--ff-radius-xl)',
            background: 'var(--ff-offwhite)',
          }}
        />
      ) : null}

      {/* Meta pills — §8.5 */}
      <Group gap="xs" wrap="wrap">
        <Badge variant="light" color="gray" tt="none" size="lg">
          ⏱ {minutesLabel(recipe.recipe.totalMinutes)}
        </Badge>
        {recipe.recipe.difficultyLabel ? (
          <Badge variant="light" color="gray" tt="none" size="lg">
            {recipe.recipe.difficultyLabel}
          </Badge>
        ) : null}
        {recipe.recipe.prepMinutes ? (
          <Badge variant="light" color="gray" tt="none" size="lg">
            Prep {minutesLabel(recipe.recipe.prepMinutes)}
          </Badge>
        ) : null}
        {recipe.recipe.servingSizeG ? (
          <Badge variant="light" color="gray" tt="none" size="lg">
            {recipe.recipe.servingSizeG} g per portion
          </Badge>
        ) : null}
      </Group>

      {recipe.recipe.description ? (
        <Text c="var(--ff-muted)" maw={680}>
          {recipe.recipe.description}
        </Text>
      ) : null}

      {/* Serving switcher — §8.7. Selected segment is ink, not brand green (§3.2). */}
      {recipe.yields.length > 1 ? (
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Servings
          </Text>
          <SegmentedControl
            value={String(activeServings)}
            onChange={(value) => setServingPreference(Number(value))}
            color="dark"
            data={recipe.yields.map((y) => ({
              label: `${y.servings}`,
              value: String(y.servings),
            }))}
            aria-label="Choose serving size"
          />
        </Stack>
      ) : null}

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xl">
        {/* Ingredients — §8.9 */}
        <Stack gap="md">
          <Title order={3}>Ingredients</Title>

          <Stack gap="sm">
            <Text fw={600} size="sm" c="var(--ff-muted)">
              In your box
            </Text>
            {boxed.map((ingredient) => (
              <IngredientRow
                key={ingredient.hfId}
                name={ingredient.name}
                amount={amounts.get(ingredient.hfId) ?? '—'}
                imageKey={ingredient.imageKey}
              />
            ))}
          </Stack>

          {pantry.length > 0 ? (
            <Card
              radius="lg"
              padding="md"
              style={{ background: PANTRY_BG, border: '1px dashed var(--ff-green-700)' }}
            >
              <Stack gap="sm">
                <Text fw={600} size="sm">
                  From your cupboard
                </Text>
                <Text size="xs" c="var(--ff-muted)">
                  Not delivered with the recipe — buy these only if you need them.
                </Text>
                {pantry.map((ingredient) => (
                  <IngredientRow
                    key={ingredient.hfId}
                    name={ingredient.name}
                    amount={amounts.get(ingredient.hfId) ?? '—'}
                    imageKey={ingredient.imageKey}
                  />
                ))}
              </Stack>
            </Card>
          ) : null}

          <Button variant="light" radius="xl" loading={busy} onClick={() => void addToShoppingList()}>
            Add to a shopping list
          </Button>
        </Stack>

        {/* Nutrition — §8.8 */}
        <Stack gap="md">
          <Title order={3}>Nutrition</Title>
          {/* Nutrition is per serving as published; it does NOT change with the switcher. */}
          <Text size="xs" c="var(--ff-muted)">
            Per serving, as published by HelloFresh.
          </Text>

          <SimpleGrid cols={2} spacing="sm">
            {MACRO_NAMES.map((name) => {
              const row = recipe.nutrition.find((n) => n.name === name);
              return (
                <Stack key={name} gap={0}>
                  <Text fw={700} style={{ fontSize: 18 }}>
                    {row?.amount != null ? `${row.amount}${row.unit ?? ''}` : '—'}
                  </Text>
                  <Text size="xs" c="var(--ff-muted)">
                    {name}
                  </Text>
                </Stack>
              );
            })}
          </SimpleGrid>

          <Table variant="vertical" withRowBorders={false} fz="sm">
            <Table.Tbody>
              {recipe.nutrition.map((row) => {
                const isSubRow = row.name.toLowerCase().startsWith('of which');
                return (
                  <Table.Tr key={row.name}>
                    <Table.Td
                      style={{
                        color: 'var(--ff-muted)',
                        paddingLeft: isSubRow ? 24 : undefined,
                        fontSize: isSubRow ? 12 : undefined,
                      }}
                    >
                      {row.name}
                    </Table.Td>
                    <Table.Td ta="right" fw={500}>
                      {row.amount != null ? `${row.amount} ${row.unit ?? ''}`.trim() : '—'}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>

          {/* Allergens — §8.11 */}
          <Title order={3} mt="md">
            Allergens
          </Title>
          {recipe.allergens.length === 0 ? (
            <Text size="sm" c="var(--ff-muted)">
              No allergen information available for this recipe.
            </Text>
          ) : (
            <Stack gap="xs">
              {trueAllergens.length > 0 ? (
                <Group gap="xs">
                  {trueAllergens.map((allergen) => (
                    <Badge key={allergen.slug} color="yellow" variant="light" tt="none">
                      {allergen.name}
                    </Badge>
                  ))}
                </Group>
              ) : null}
              {/* Traces are ranked below and reported quietly — conflating them overstates risk. */}
              {traces.length > 0 ? (
                <>
                  <Text size="xs" c="var(--ff-muted)">
                    May contain traces of
                  </Text>
                  <Group gap="xs">
                    {traces.map((allergen) => (
                      <Badge key={allergen.slug} color="gray" variant="light" tt="none" size="sm">
                        {allergen.name}
                      </Badge>
                    ))}
                  </Group>
                </>
              ) : null}
            </Stack>
          )}

          {/* Utensils — §8.12 */}
          {recipe.utensils.length > 0 ? (
            <>
              <Title order={3} mt="md">
                You will need
              </Title>
              <Group gap="xs">
                {recipe.utensils.map((utensil) => (
                  <Badge key={utensil.name} variant="light" color="gray" tt="none">
                    {utensil.name}
                  </Badge>
                ))}
              </Group>
            </>
          ) : null}
        </Stack>
      </SimpleGrid>

      {/* Steps — §8.10 */}
      {recipe.steps.length > 0 ? (
        <Stack gap="md">
          <Title order={3}>Method</Title>
          <div
            style={{
              display: 'flex',
              gap: 16,
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              paddingBottom: 8,
            }}
          >
            {recipe.steps.map((step, index) => {
              const stepImage = imageUrl(step.imageKey);
              return (
                <Card
                  key={step.stepIndex}
                  withBorder
                  radius="lg"
                  padding="md"
                  style={{
                    minWidth: 260,
                    maxWidth: 320,
                    flex: '0 0 auto',
                    scrollSnapAlign: 'start',
                  }}
                >
                  <Stack gap="xs">
                    {stepImage ? (
                      <img
                        src={stepImage}
                        alt=""
                        loading="lazy"
                        style={{
                          width: '100%',
                          aspectRatio: '4 / 3',
                          objectFit: 'cover',
                          borderRadius: 'var(--ff-radius-md)',
                        }}
                      />
                    ) : null}
                    <Text size="xs" c="var(--ff-muted)">
                      Step {index + 1} of {recipe.steps.length}
                    </Text>
                    {step.caption ? (
                      <Text fw={600} style={{ fontSize: 17 }}>
                        {step.caption}
                      </Text>
                    ) : null}
                    {/* Plain text, not raw scraped HTML — this is untrusted third-party markup. */}
                    <Text size="sm" c="var(--ff-muted)">
                      {step.instructionsText}
                    </Text>
                  </Stack>
                </Card>
              );
            })}
          </div>
        </Stack>
      ) : null}

      <Divider />

      <Text size="xs" c="var(--ff-muted)">
        Imported from{' '}
        <Anchor href={recipe.recipe.sourceUrl} target="_blank" rel="noreferrer" size="xs">
          the original page
        </Anchor>
        . This copy lives in your planner.
      </Text>
    </PageShell>
  );

  function setServingPreference(next: number) {
    setServings(next);
  }
}

function IngredientRow({
  name,
  amount,
  imageKey,
}: {
  name: string;
  amount: string;
  imageKey: string | null;
}) {
  const src = imageUrl(imageKey);
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Group gap="sm" wrap="nowrap">
        {src ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            width={28}
            height={28}
            style={{ borderRadius: '50%', objectFit: 'cover', background: 'var(--ff-offwhite)' }}
          />
        ) : (
          <ThemeIcon size={28} radius="xl" variant="light" color="gray" aria-hidden="true">
            <span style={{ fontSize: 12 }}>•</span>
          </ThemeIcon>
        )}
        <Text size="sm">{name}</Text>
      </Group>
      <Text fw={600} size="sm" style={{ whiteSpace: 'nowrap' }}>
        {amount}
      </Text>
    </Group>
  );
}
